import {
  CaseProgressBySectionStatNode,
  GetLensSbrStatsDocument,
  OverallProgressBySectionStatNode,
  GetPendingCasesStatsResult,
  TimeUnit,
  SectionBasedReviewStatsNode,
  CompletedCasesDatedStats,
  GetReviewerLensSbrStatsDocument,
  ReviewerCaseProgressBySectionStatNode,
  CompletedCasesAndSectionStats,
} from "generated/graphql";
import { runGqlQuery } from "gqlClient";
import { useState } from "react";

interface UseLensStats {
  getSBRStats: (body: {
    unit: TimeUnit;
    backPeriod: number;
    sectionId?: number;
    assigneeId?: number;
    firmId?: number;
  }) => Promise<void>;
  getReviewerSBRStats: (body: {
    unit: TimeUnit;
    backPeriod: number;
    sectionId?: number;
    assigneeId: number;
    firmId?: number;
  }) => Promise<void>;
  overallCaseProgressStats?: OverallProgressBySectionStatNode;
  caseProgressByReviewerStats: GetPendingCasesStatsResult;
  caseProgressByReviewerTotal: number;
  caseProgressBySectionStats: CaseProgressBySectionStatNode[];
  completedCaseStats: CompletedCasesDatedStats[];
  completedCaseStatsTotal: number;
  completedSections: number;
  completedSectionsAndCases: number;
  caseProgressBySectionStatsTotal: number;
  reviewerCaseProgressBySection: ReviewerCaseProgressBySectionStatNode[];
  reviewerCompletedCaseStats?: CompletedCasesAndSectionStats;
  reviewerCaseProgressBySectionStatsTotal: number;
  reviewerCompletedSectionsAndCases: number;
  overallCompletedCases: number;
  overallValidationCases: number;
  overallReviewCases: number;
}

export type AdminStats = Omit<
  SectionBasedReviewStatsNode,
  "reviewerCaseProgressBySection" | "reviewerCompletedCaseStats"
>;

export type ReviewerStats = Pick<
  SectionBasedReviewStatsNode,
  "reviewerCaseProgressBySection" | "reviewerCompletedCaseStats"
>;

export function useLensStats(lensId: number): UseLensStats {
  const [stats, setStats] = useState<AdminStats | undefined>(undefined);
  const [reviewerStats, setReviewerStats] = useState<ReviewerStats | undefined>(undefined);
  const completedSections =
    stats?.caseProgressBySectionStats.reduce((count, stat) => count + stat.completeCount, 0) ?? 0;
  const completedSectionsAndCases = completedSections + (stats?.overallCaseProgressStats?.completedCasesCount ?? 0);
  const reviewerCompletedSectionsAndCases =
    (reviewerStats?.reviewerCompletedCaseStats?.completedSections ?? 0) +
    (reviewerStats?.reviewerCompletedCaseStats?.completedCases ?? 0);

  async function getSBRStats({
    unit,
    backPeriod,
    sectionId,
    assigneeId,
    firmId,
  }: {
    unit: TimeUnit;
    backPeriod: number;
    sectionId?: number;
    assigneeId?: number;
    firmId?: number;
  }): Promise<void> {
    const response = await runGqlQuery(GetLensSbrStatsDocument, {
      lensId,
      sectionId,
      assigneeId,
      interval: {
        unit,
        backPeriod,
      },
      firmId,
    });

    setStats(response.lens.sectionBasedReviewStats);
  }

  async function getReviewerSBRStats({
    unit,
    backPeriod,
    sectionId,
    assigneeId,
    firmId,
  }: {
    unit: TimeUnit;
    backPeriod: number;
    sectionId?: number;
    assigneeId: number;
    firmId?: number;
  }): Promise<void> {
    const response = await runGqlQuery(GetReviewerLensSbrStatsDocument, {
      lensId,
      sectionId,
      assigneeId,
      firmId,
      interval: {
        unit,
        backPeriod,
      },
    });

    setReviewerStats(response.lens.sectionBasedReviewStats);
  }

  const caseProgressBySectionStatsTotal =
    stats?.caseProgressBySectionStats.reduce(
      (count, stat) => count + (stat.completeCount ?? 0) + (stat.reviewCount ?? 0) + (stat.validationCount ?? 0),
      0
    ) ?? 0;

  const reviewerCaseProgressBySectionStatsTotal =
    reviewerStats?.reviewerCaseProgressBySection.reduce(
      (count, stat) => count + (stat.completeCount ?? 0) + (stat.reviewCount ?? 0) + (stat.validationCount ?? 0),
      0
    ) ?? 0;
  const caseProgressByReviewerStats = stats?.caseProgressByReviewerStats ?? {
    byAssignee: [],
    unassignedCount: 0,
  };
  const caseProgressByReviewerTotal =
    caseProgressByReviewerStats.byAssignee.reduce((count, curr) => count + curr.count, 0) +
    caseProgressByReviewerStats.unassignedCount;
  const completedCaseStats = stats?.completedCaseStats ?? [];
  const completedCaseStatsTotal = completedCaseStats[completedCaseStats.length - 1]?.accumulatedCount ?? 0;

  return {
    getSBRStats,
    getReviewerSBRStats,
    overallCaseProgressStats: stats?.overallCaseProgressStats,
    overallCompletedCases: stats?.overallCaseProgressStats.completedCasesCount ?? 0,
    overallValidationCases: stats?.overallCaseProgressStats.validationCasesCount ?? 0,
    overallReviewCases: stats?.overallCaseProgressStats.reviewCasesCount ?? 0,
    reviewerCaseProgressBySection: reviewerStats?.reviewerCaseProgressBySection ?? [],
    reviewerCompletedCaseStats: reviewerStats?.reviewerCompletedCaseStats,
    caseProgressByReviewerStats,
    caseProgressByReviewerTotal,
    caseProgressBySectionStats: stats?.caseProgressBySectionStats ?? [],
    completedCaseStats,
    completedCaseStatsTotal,
    completedSections,
    completedSectionsAndCases,
    caseProgressBySectionStatsTotal,
    reviewerCaseProgressBySectionStatsTotal,
    reviewerCompletedSectionsAndCases,
  };
}
